import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import api from "../utils/api";
import { getRelativeRange } from "../hooks/useHistoricalData";

// Compliance colour bands: 90% and up green, 50-89% amber, under 50% red.
const complianceColor = (pct) => {
  const n = Number(pct);
  if (!Number.isFinite(n)) return "#64748b";
  if (n >= 90) return "#16a34a";
  if (n >= 50) return "#f59e0b";
  return "#dc2626";
};

// Table styling mirrors the Live table (channelStyles in LiveTab.js).
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle = {
  padding: "12px 8px",
  textAlign: "center",
  borderBottom: "2px solid #dee2e6",
  backgroundColor: "#f8f9fa",
  fontWeight: 600,
  whiteSpace: "nowrap",
};
const tdStyle = {
  padding: "10px 8px",
  textAlign: "center",
  borderBottom: "1px solid #dee2e6",
};

// A room with no data in the range comes back with nulls.
const fmt = (v, digits = 1) =>
  v === null || v === undefined || Number.isNaN(Number(v))
    ? "-"
    : Number(v).toFixed(digits);

const pad2 = (n) => String(n).padStart(2, "0");
const toInputDate = (d) =>
  !d || Number.isNaN(d.getTime())
    ? ""
    : `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const QUICK_RANGES = [
  { label: "1D", days: 1 },
  { label: "3D", days: 3 },
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
];

const quickRangeStyle = {
  fontSize: "11px",
  padding: "6px 12px",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  background: "#f8fafc",
  cursor: "pointer",
  fontWeight: 700,
  color: "#475569",
};

const SummaryCard = ({ label, value, unit, sub, color, bg }) => (
  <div
    style={{
      flex: "1 1 200px",
      minWidth: 180,
      background: bg,
      borderRadius: 12,
      padding: "14px 16px",
      border: "1px solid rgba(15,23,42,0.06)",
    }}
  >
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.05em",
        color,
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
    <div style={{ marginTop: 6, fontSize: 26, fontWeight: 800, color }}>
      {value}
      {unit && (
        <span style={{ fontSize: 13, fontWeight: 700, marginLeft: 4 }}>{unit}</span>
      )}
    </div>
    {sub && (
      <div style={{ marginTop: 4, fontSize: 12, color: "#64748b" }}>{sub}</div>
    )}
  </div>
);

const EnvironmentEfficiency = ({ username }) => {
  // Energy's own range rule (full days ending yesterday), so the default is
  // exactly what the 7D chip produces and both tabs show the same dates.
  const defaultRange = () => getRelativeRange(7);

  // draft vs applied, same pattern as the Historical filter bar: typing a date
  // must not fire a request.
  const [range, setRange] = useState(defaultRange);
  const [draftRange, setDraftRange] = useState(defaultRange);

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const startStr = toInputDate(range.start);
  const endStr = toInputDate(range.end);
  const draftStartStr = toInputDate(draftRange.start);
  const draftEndStr = toInputDate(draftRange.end);

  const isDirty = draftStartStr !== startStr || draftEndStr !== endStr;
  const isValid =
    !!draftStartStr && !!draftEndStr && draftRange.start <= draftRange.end;
  const canApply = isDirty && isValid;

  const applyRange = () => {
    if (!canApply) return;
    setRange({ start: draftRange.start, end: draftRange.end });
  };

  // Quick ranges only fill the date boxes; Apply fetches, same as energy.
  const pickQuickRange = (days) => setDraftRange(getRelativeRange(days));

  const fetchData = useCallback(async () => {
    if (!username || !startStr) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/temperature-efficiency", {
        params: { username, start_date: startStr, end_date: endStr },
      });
      setPayload(res.data && res.data.status === "success" ? res.data : null);
      if (res.data && res.data.status !== "success") {
        setError("Could not load efficiency data.");
      }
    } catch (err) {
      console.error("Fetch temperature-efficiency error:", err);
      setError("Failed to load efficiency data.");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [username, startStr, endStr]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const summary = payload?.summary || {};
  // Backend already sorts worst first -- never re-sort here.
  const rooms = useMemo(() => payload?.rooms || [], [payload]);
  const trend = useMemo(() => payload?.trend || [], [payload]);

  // Each trend day is { date, rooms: { roomName: pct } }. Flip that into one
  // series per room so every room becomes a line.
  const chart = useMemo(() => {
    const dates = trend.map((t) => t.date);
    const names = [];
    trend.forEach((t) => {
      Object.keys(t.rooms || {}).forEach((n) => {
        if (!names.includes(n)) names.push(n);
      });
    });
    // Colours come from Node Settings via the payload. Until every room carries
    // one, ApexCharts picks its own.
    const configured = {};
    rooms.forEach((r) => {
      if (r.location && r.color) configured[r.location] = r.color;
    });
    const colors = names.map((n) => configured[n]).filter(Boolean);
    return {
      series: names.map((name) => ({
        name,
        // null keeps a gap in the line instead of dropping to zero on a day
        // the room reported nothing.
        data: trend.map((t) => {
          const v = (t.rooms || {})[name];
          return v === null || v === undefined ? null : Number(v);
        }),
      })),
      options: {
        ...(colors.length === names.length ? { colors } : {}),
        chart: {
          type: "line",
          toolbar: { show: false },
          fontFamily: "inherit",
          animations: { enabled: false },
        },
        stroke: { curve: "smooth", width: 2 },
        markers: { size: 3 },
        dataLabels: { enabled: false },
        legend: { position: "bottom", fontSize: "11px" },
        grid: { borderColor: "#eef2f7", strokeDashArray: 4 },
        xaxis: {
          categories: dates,
          labels: { style: { fontSize: "11px", colors: "#94a3b8" } },
        },
        yaxis: {
          min: 0,
          max: 100,
          title: { text: "Compliance (%)", style: { fontSize: "12px" } },
          labels: { formatter: (v) => `${Number(v).toFixed(0)}%` },
        },
        tooltip: {
          shared: true,
          y: { formatter: (v) => (v === null ? "-" : `${Number(v).toFixed(1)}%`) },
        },
      },
    };
  }, [trend, rooms]);

  const hasRooms = rooms.length > 0;

  return (
    <>
      {/* ===== Filter bar ===== */}
      <div className="env-heatmap-section">
        <div
          className="env-heatmap-controls"
          style={{
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
            justifyContent: "flex-start",
            borderBottom: "none",
            paddingBottom: 0,
          }}
        >
          <div className="env-datetime-group">
            <input
              type="date"
              className="env-date-input"
              value={draftStartStr}
              onChange={(e) => {
                const v = new Date(`${e.target.value}T00:00:00`);
                if (!Number.isNaN(v.getTime()))
                  setDraftRange((p) => ({ ...p, start: v }));
              }}
            />
            <span className="env-date-separator">TO</span>
            <input
              type="date"
              className="env-date-input"
              value={draftEndStr}
              onChange={(e) => {
                const v = new Date(`${e.target.value}T00:00:00`);
                if (!Number.isNaN(v.getTime()))
                  setDraftRange((p) => ({ ...p, end: v }));
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 6 }}>
            {QUICK_RANGES.map((q) => (
              <button
                key={q.days}
                type="button"
                onClick={() => pickQuickRange(q.days)}
                style={quickRangeStyle}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#fff")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#f8fafc")}
              >
                {q.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={applyRange}
            disabled={!canApply}
            title={isDirty && !isValid ? "From must be on or before To" : undefined}
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              border: "none",
              background: canApply ? "#1BA08E" : "#e2e8f0",
              color: canApply ? "#fff" : "#94a3b8",
              fontSize: 13,
              fontWeight: 700,
              cursor: canApply ? "pointer" : "not-allowed",
            }}
          >
            Apply
          </button>
        </div>
      </div>

      {loading && !payload ? (
        <div className="env-heatmap-section">
          <div style={{ padding: 32, textAlign: "center", color: "#64748b" }}>
            Loading efficiency...
          </div>
        </div>
      ) : error ? (
        <div className="env-heatmap-section">
          <div style={{ padding: 32, textAlign: "center", color: "#dc2626" }}>
            {error}
          </div>
        </div>
      ) : !hasRooms ? (
        // Only rooms with Standard / UCL / LCL configured are returned.
        <div className="env-heatmap-section">
          <div style={{ padding: 32, textAlign: "center", color: "#64748b" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🌡️</div>
            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>
              No rooms to report on
            </div>
            <div style={{ fontSize: 13 }}>
              Efficiency only covers rooms that have <strong>Standard</strong>,{" "}
              <strong>UCL</strong> and <strong>LCL</strong> set. Add those in
              Info &rarr; Node Settings for each temperature sensor.
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* ===== Summary cards ===== */}
          <div
            className="env-heatmap-section"
            style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}
          >
            <SummaryCard
              label="Overall Compliance"
              value={fmt(summary.overall_compliance_pct)}
              unit="%"
              sub={`${payload?.from || startStr} to ${payload?.to || endStr}`}
              color={complianceColor(summary.overall_compliance_pct)}
              bg="#f0fdf4"
            />
            <SummaryCard
              label="Rooms Monitored"
              value={summary.rooms_monitored ?? "-"}
              sub="With limits configured"
              color="#1e88e5"
              bg="#eff6ff"
            />
            <SummaryCard
              label="Rooms Out of Range"
              value={summary.rooms_out_of_range ?? "-"}
              sub="Below 100% compliance"
              color="#dc2626"
              bg="#fef2f2"
            />
            <SummaryCard
              label="Worst Room"
              value={summary.worst_room?.location || "-"}
              sub={
                summary.worst_room
                  ? `${fmt(summary.worst_room.compliance_pct)}% compliance`
                  : undefined
              }
              color="#b45309"
              bg="#fffbeb"
            />
          </div>

          {/* ===== Room table ===== */}
          <div className="env-heatmap-section">
            <div style={{ overflowX: "auto" }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: "left" }}>Room</th>
                    <th style={thStyle}>Target (°C)</th>
                    <th style={thStyle}>Avg Temp (°C)</th>
                    <th style={thStyle}>Avg Deviation (°C)</th>
                    <th style={thStyle}>Compliance %</th>
                    <th style={thStyle}>Hours Out of Range</th>
                  </tr>
                </thead>
                <tbody>
                  {rooms.map((r, idx) => (
                    <tr
                      key={r.meter_id || `${r.location}-${idx}`}
                      style={{ backgroundColor: idx % 2 === 1 ? "#f8f9fa" : "#fff" }}
                    >
                      <td style={{ ...tdStyle, textAlign: "left", fontWeight: 600 }}>
                        {r.location || "-"}
                      </td>
                      <td style={tdStyle}>{fmt(r.target_temp)}</td>
                      <td style={tdStyle}>{fmt(r.avg_temp)}</td>
                      <td style={tdStyle}>{fmt(r.avg_deviation, 2)}</td>
                      <td
                        style={{
                          ...tdStyle,
                          fontWeight: 700,
                          color: complianceColor(r.compliance_pct),
                        }}
                      >
                        {fmt(r.compliance_pct)}
                        {r.compliance_pct === null || r.compliance_pct === undefined
                          ? ""
                          : "%"}
                      </td>
                      <td style={tdStyle}>{fmt(r.hours_out_of_range)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ===== Daily compliance trend ===== */}
          {chart.series.length > 0 && (
            <div className="env-heatmap-section">
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#0f172a",
                  marginBottom: 4,
                }}
              >
                Daily Compliance by Room
              </div>
              <ReactApexChart
                options={chart.options}
                series={chart.series}
                type="line"
                height={340}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default EnvironmentEfficiency;
